import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CalendarSvg } from '../../images/menuIcons/main/mb-calendar.svg';
import { ReactComponent as BookingsSvg } from '../../images/menuIcons/main/mb-bookings.svg';
import { ReactComponent as MembershipsSvg } from '../../images/menuIcons/main/mb-memberships.svg';
import { ReactComponent as VouchersSvg } from '../../images/menuIcons/main/mb-vouchers.svg';
import { ReactComponent as ExternalVouchersSvg } from '../../images/menuIcons/main/mb-external-vouchers.svg';
import { ReactComponent as PaymentLinksSvg } from '../../images/menuIcons/main/mb-payment-links.svg';
import { ReactComponent as WaitlistsSvg } from '../../images/menuIcons/main/mb-waitlists.svg';
import { ReactComponent as WorkshopsSvg } from '../../images/menuIcons/main/mb-workshops.svg';
import { ReactComponent as DiscountsSvg } from '../../images/menuIcons/main/mb-discounts.svg';
import { ReactComponent as MiniSitesSvg } from '../../images/menuIcons/main/mb-mini-sites.svg';
import { ReactComponent as SystemSettingsSvg } from '../../images/menuIcons/main/mb-system-settings.svg';
import { ReactComponent as UserSettingsSvg } from '../../images/menuIcons/main/mb-user-settings.svg';
import { ReactComponent as ReportsSvg } from '../../images/menuIcons/main/mb-reports.svg';
import { ReactComponent as SalesDataSvg } from '../../images/menuIcons/main/mb-sales-data.svg';
import { ReactComponent as ActivityLogSvg } from '../../images/menuIcons/main/mb-activity-log.svg';
import { ReactComponent as LogoutSvg } from '../../images/menuIcons/main/mb-logout.svg';
import { ReactComponent as GeneralSvg } from '../../images/menuIcons/secondary/mb-general.svg';
import { ReactComponent as BusinessSvg } from '../../images/menuIcons/secondary/mb-business.svg';
import { ReactComponent as CatalogSvg } from '../../images/menuIcons/secondary/mb-catalog.svg';
import { ReactComponent as EmailTemplatesSvg } from '../../images/menuIcons/secondary/mb-email-templates.svg';
import { ReactComponent as WhatsappSvg } from '../../images/menuIcons/secondary/mb-whatsapp.svg';
import { ReactComponent as WebhooksSvg } from '../../images/menuIcons/secondary/mb-webhooks.svg';
import { ReactComponent as QrBoardsSvg } from '../../images/menuIcons/secondary/mb-qr-boards.svg';
import { ReactComponent as UsersSvg } from '../../images/menuIcons/secondary/mb-users.svg';

export enum SidebarIconName {
  Calendar = 'calendar',
  Bookings = 'bookings',
  Memberships = 'memberships',
  Vouchers = 'vouchers',
  ExternalVouchers = 'externalVouchers',
  PaymentLinks = 'paymentLinks',
  Waitlists = 'waitlists',
  Workshops = 'workshops',
  Discounts = 'discounts',
  MiniSites = 'miniSites',
  SystemSettings = 'systemSettings',
  UserSettings = 'userSettings',
  Reports = 'reports',
  SalesData = 'salesData',
  ActivityLog = 'activityLog',
  Logout = 'logout',
  General = 'general',
  Business = 'business',
  Catalog = 'catalog',
  EmailTemplates = 'emailTemplates',
  Whatsapp = 'whatsapp',
  Webhooks = 'webhooks',
  QrBoards = 'qrBoards',
  Users = 'users',
}

const SIDEBAR_ICONS: Record<SidebarIconName, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [SidebarIconName.Calendar]: CalendarSvg,
  [SidebarIconName.Bookings]: BookingsSvg,
  [SidebarIconName.Memberships]: MembershipsSvg,
  [SidebarIconName.Vouchers]: VouchersSvg,
  [SidebarIconName.ExternalVouchers]: ExternalVouchersSvg,
  [SidebarIconName.PaymentLinks]: PaymentLinksSvg,
  [SidebarIconName.Waitlists]: WaitlistsSvg,
  [SidebarIconName.Workshops]: WorkshopsSvg,
  [SidebarIconName.Discounts]: DiscountsSvg,
  [SidebarIconName.MiniSites]: MiniSitesSvg,
  [SidebarIconName.SystemSettings]: SystemSettingsSvg,
  [SidebarIconName.UserSettings]: UserSettingsSvg,
  [SidebarIconName.Reports]: ReportsSvg,
  [SidebarIconName.SalesData]: SalesDataSvg,
  [SidebarIconName.ActivityLog]: ActivityLogSvg,
  [SidebarIconName.Logout]: LogoutSvg,
  [SidebarIconName.General]: GeneralSvg,
  [SidebarIconName.Business]: BusinessSvg,
  [SidebarIconName.Catalog]: CatalogSvg,
  [SidebarIconName.EmailTemplates]: EmailTemplatesSvg,
  [SidebarIconName.Whatsapp]: WhatsappSvg,
  [SidebarIconName.Webhooks]: WebhooksSvg,
  [SidebarIconName.QrBoards]: QrBoardsSvg,
  [SidebarIconName.Users]: UsersSvg,
};

const MIRRORED_IN_LTR = new Set([SidebarIconName.Logout, SidebarIconName.ExternalVouchers]);

export const SidebarIcon = ({ name, size = 22 }: { name: SidebarIconName; size?: number }) => {
  const { i18n } = useTranslation();
  const Svg = SIDEBAR_ICONS[name];
  return (
    <Svg
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{
        flexShrink: 0,
        display: 'block',
        transform: MIRRORED_IN_LTR.has(name) && i18n.dir() === 'ltr' ? 'scaleX(-1)' : undefined,
      }}
    />
  );
};
